import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as LucideIcons from 'lucide-react';
import { MapPin, Search, ChevronDown, Star, Sparkles } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { businessesAPI } from '@/lib/api';
import { openExternalBookviaFlow } from '@/lib/capacitor';
import { getSelectedCity } from '@/lib/selectedCity';

// Resolve a lucide icon component from a category's `icon` field. Accepts
// kebab-case ("paw-print"), PascalCase ("PawPrint"), or empty. Falls back
// to <Sparkles/> so a new category always renders even without an icon set.
function toPascal(str) {
  if (!str) return '';
  return String(str)
    .split(/[-_ ]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join('');
}
function IconForCategory({ icon, className, style }) {
  const key = toPascal(icon);
  const Cmp = (key && LucideIcons[key]) || LucideIcons.Sparkles;
  return <Cmp className={className} style={style} strokeWidth={2} />;
}

const CATEGORY_CHIPS = null; // Categories now come from the DB via props.

// Base64 grain SVG for subtle texture over the background — makes it feel
// "crafted" and not like a flat template. 2% opacity keeps it invisible on
// most screens but breaks the plastic look on Retina/OLED.
const NOISE_URI =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.16 0 0 0 0 0.16 0 0 0 0 0.16 0 0 0 0.06 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")";

/**
 * Mobile city pill — tapping it navigates to the full-page `/select-city`.
 * The bottom-sheet approach was scrapped because the mobile keyboard would
 * always cover the modal content in one way or another; a native full-page
 * flow sidesteps every one of those edge cases.
 *
 * We still auto-detect the user's city once on first mount (only when the
 * user has no persisted pick), so first-time visitors get a sensible
 * default without ever opening the picker.
 */
function CityPill({ city, onChange, currentPath }) {
  const { language } = useI18n();
  const navigate = useNavigate();
  const [detecting, setDetecting] = useState(false);

  // Silent one-shot auto-detect on first mount only when no city is
  // persisted. Manual re-detects live on the /select-city page.
  useEffect(() => {
    if (getSelectedCity()) return;
    if (!navigator.geolocation) return;
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const r = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&accept-language=es`,
            { headers: { 'User-Agent': 'Bookvia/1.0' } }
          );
          const d = await r.json();
          const detected = d?.address?.city || d?.address?.town || d?.address?.village || d?.address?.state;
          if (detected && !getSelectedCity()) onChange(detected);
        } catch { /* ignore */ }
        setDetecting(false);
      },
      () => setDetecting(false),
      { timeout: 6000, maximumAge: 5 * 60 * 1000 }
    );
  }, [onChange]);

  const goToPicker = () => {
    const returnTo = encodeURIComponent(currentPath || '/');
    navigate(`/select-city?returnTo=${returnTo}`);
  };

  return (
    <motion.button
      whileTap={{ scale: 0.96 }}
      onClick={goToPicker}
      data-testid="mobile-city-selector"
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/70 dark:bg-white/5 backdrop-blur-md border border-black/5 dark:border-white/10 text-[13px] font-semibold text-[#2A2A2A] dark:text-[#F5F2ED] shadow-[0_2px_8px_rgba(42,42,42,0.04)]"
    >
      <MapPin className="h-3.5 w-3.5 text-[#E85A4F]" strokeWidth={2.5} />
      <span className="max-w-[150px] truncate">
        {detecting && !city
          ? (language === 'es' ? 'Ubicando…' : 'Locating…')
          : (city || (language === 'es' ? 'Elegir ciudad' : 'Choose city'))}
      </span>
      <ChevronDown className="h-3.5 w-3.5 opacity-60" />
    </motion.button>
  );
}

function ActivityTicker({ activities, language }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (!activities.length) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % activities.length), 4500);
    return () => clearInterval(t);
  }, [activities.length]);
  if (!activities.length) return null;
  const a = activities[idx];
  return (
    <div className="px-5 mt-3">
      <AnimatePresence mode="wait">
        <motion.div
          key={idx}
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -10, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 24 }}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 dark:bg-white/5 backdrop-blur-md border border-black/5 dark:border-white/10 text-[12px] font-medium text-[#737373] dark:text-[#A39F99]"
          data-testid="mobile-activity-ticker"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#E85A4F] opacity-60" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#E85A4F]" />
          </span>
          <span className="truncate max-w-[280px]">
            {language === 'es'
              ? `${a.name} reservó en ${a.business} · ${a.time}`
              : `${a.name} booked at ${a.business} · ${a.time}`}
          </span>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function FeaturedCard({ business, onClick }) {
  const img = business.image || business.cover_image || business.logo_url || 'https://images.unsplash.com/photo-1560066984-138dadb4c035?auto=format&fit=crop&q=80&w=800';
  const rating = business.average_rating || business.rating || 0;
  return (
    <motion.button
      whileTap={{ scale: 0.97 }}
      onClick={onClick}
      className="shrink-0 w-64 rounded-2xl overflow-hidden bg-white dark:bg-[#1E1A17] shadow-[0_4px_16px_rgba(42,42,42,0.06)] border border-[#EAE3D9] dark:border-[#332D29] text-left snap-start"
      data-testid={`mobile-featured-${business.id}`}
    >
      <div className="relative h-36 w-full bg-[#F3EFE9] dark:bg-[#2A2522]">
        <img
          src={img}
          alt={business.name}
          className="h-full w-full object-cover"
          loading="lazy"
        />
        {rating > 0 && (
          <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/70 backdrop-blur text-white text-[11px] font-semibold">
            <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
            {Number(rating).toFixed(1)}
          </div>
        )}
      </div>
      <div className="px-3 py-2.5">
        <p className="font-display font-bold text-[15px] text-[#2A2A2A] dark:text-[#F5F2ED] truncate">
          {business.name}
        </p>
        <p className="text-[11px] font-medium uppercase tracking-wider text-[#A3A3A3] dark:text-[#736F6A] truncate">
          {business.category_name || business.category || ''}
        </p>
        <div className="mt-1 flex items-center justify-between text-[12px] text-[#737373] dark:text-[#A39F99]">
          <span className="truncate">{business.city || ''}</span>
          {business.min_price ? (
            <span className="font-semibold text-[#E85A4F]">${Number(business.min_price).toFixed(0)}</span>
          ) : null}
        </div>
      </div>
    </motion.button>
  );
}

function BottomTab({ icon: Icon, label, active, onClick, testid }) {
  return (
    <motion.button
      whileTap={{ scale: 0.9 }}
      onClick={onClick}
      className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2"
      data-testid={testid}
    >
      <motion.div
        animate={active ? { scale: [1, 1.15, 1] } : { scale: 1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 18 }}
      >
        <Icon
          className={`h-[22px] w-[22px] ${active ? 'text-[#E85A4F]' : 'text-[#737373] dark:text-[#A39F99]'}`}
          strokeWidth={active ? 2.5 : 2}
        />
      </motion.div>
      <span className={`text-[10px] font-medium ${active ? 'text-[#E85A4F]' : 'text-[#737373] dark:text-[#A39F99]'}`}>
        {label}
      </span>
    </motion.button>
  );
}

export function MobileHomeExperience({
  city,
  setCity,
  cities,
  categories,
  featuredBusinesses,
  onCategoryClick,
  onFeaturedClick,
  onSearchClick,
}) {
  const { language } = useI18n();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [topRated, setTopRated] = useState([]);

  // Only show top-level categories (parent_id == null) with business_count > 0
  // first, then any remaining top-level. Keeps the list compact & relevant.
  const chipCategories = useMemo(() => {
    const list = Array.isArray(categories) ? categories : [];
    const topLevel = list.filter((c) => !c.parent_id);
    const withBiz = topLevel.filter((c) => (c.business_count ?? 0) > 0);
    const rest = topLevel.filter((c) => (c.business_count ?? 0) === 0);
    return [...withBiz, ...rest].slice(0, 10);
  }, [categories]);

  // Extract the first name for a short, friendly greeting.
  // The backend user model uses `full_name`, not `name`.
  const firstName = useMemo(() => {
    if (!isAuthenticated) return null;
    const raw = user?.full_name || user?.name || '';
    const first = String(raw).trim().split(/\s+/)[0];
    return first || null;
  }, [isAuthenticated, user]);

  useEffect(() => {
    // Ticker is optional — only show if backend has a `getRecentActivity`
    // endpoint AND it returns real events. Fails silently otherwise.
    if (typeof businessesAPI.getRecentActivity !== 'function') return;
    businessesAPI.getRecentActivity()
      .then((r) => {
        const list = Array.isArray(r?.data) ? r.data : [];
        setActivities(list);
      })
      .catch(() => setActivities([]));
  }, []);

  // Load top-rated businesses (min_rating 4.0) for the "Mejor valorados"
  // section. Filters by city if the user picked/detected one.
  useEffect(() => {
    const params = { limit: 8, min_rating: 4.0 };
    if (city) params.city = city;
    businessesAPI
      .search(params)
      .then((r) => {
        const raw = r?.data?.businesses || r?.data || [];
        // Exclude businesses already shown in "Featured" to avoid duplicates
        const featuredIds = new Set((featuredBusinesses || []).map((b) => b.id));
        setTopRated(raw.filter((b) => !featuredIds.has(b.id)).slice(0, 8));
      })
      .catch(() => setTopRated([]));
  }, [city, featuredBusinesses]);

  return (
    <div
      className="relative bg-[#FAF7F2] dark:bg-[#12100E] font-body pb-8"
      data-testid="mobile-home-experience"
    >
      {/* Subtle grain overlay for depth */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.55] mix-blend-multiply dark:mix-blend-screen dark:opacity-[0.35] z-0"
        style={{ backgroundImage: NOISE_URI }}
        aria-hidden="true"
      />

      {/* ── Search header (normal flow — scrolls with content) ─────
          The city pill and search bar are part of the normal document
          flow. They appear at the top on page load but scroll away with
          the rest of the content when the user scrolls down. */}
      <div
        className="relative z-10 bg-[#FAF7F2] dark:bg-[#12100E] border-b border-[#EAE3D9]/60 dark:border-[#332D29]/60 pt-3 pb-3"
        data-testid="mobile-search-header"
      >
        <div className="px-5 flex items-center gap-2">
          <div className="shrink-0">
            <CityPill city={city} onChange={setCity} currentPath={typeof window !== 'undefined' ? window.location.pathname : '/'} />
          </div>
          <motion.button
            whileTap={{ scale: 0.98 }}
            onClick={onSearchClick}
            className="flex-1 min-w-0 flex items-center gap-2 px-3 py-2.5 rounded-xl bg-white dark:bg-[#1E1A17] border border-[#EAE3D9] dark:border-[#332D29] shadow-[0_2px_10px_rgba(42,42,42,0.05)]"
            data-testid="mobile-hero-search"
          >
            <Search className="h-[18px] w-[18px] text-[#E85A4F] shrink-0" strokeWidth={2.5} />
            <span className="text-[13px] font-medium text-[#737373] dark:text-[#A39F99] truncate">
              {language === 'es' ? 'Busca negocio, servicio o categoría' : 'Search business, service or category'}
            </span>
          </motion.button>
        </div>
      </div>

      <div className="relative z-10 px-5 pt-4">
        {/* Personalized greeting (only when logged in) */}
        {firstName && (
          <p
            className="font-body text-[15px] text-[#737373] dark:text-[#A39F99]"
            data-testid="mobile-home-greeting"
          >
            {language === 'es' ? `Hola, ${firstName} 👋` : `Hi, ${firstName} 👋`}
          </p>
        )}
      </div>

      {/* ── 2. Activity Ticker (only when real activity exists) ─── */}
      <ActivityTicker activities={activities} language={language} />

      {/* ── 3. Quick Category Chips ─────────────────────────────── */}
      <div className="mt-6 relative z-10">
        <div className="px-5 flex items-center justify-between mb-3">
          <h2 className="font-display font-semibold text-[17px] text-[#2A2A2A] dark:text-[#F5F2ED]">
            {language === 'es' ? 'Explora por categoría' : 'Browse categories'}
          </h2>
          <button
            onClick={() => navigate('/categories')}
            className="text-[13px] font-semibold text-[#E85A4F]"
            data-testid="mobile-cats-see-all"
          >
            {language === 'es' ? 'Ver todas' : 'See all'}
          </button>
        </div>
        <div
          className="flex gap-3 overflow-x-auto snap-x snap-mandatory pl-5 pr-5 pb-1 scrollbar-hide"
          data-testid="mobile-category-chips"
        >
          {chipCategories.map((c) => (
            <motion.button
              key={c.id || c.slug}
              whileTap={{ scale: 0.95 }}
              onClick={() => onCategoryClick?.(c)}
              className="shrink-0 snap-start inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white dark:bg-[#1E1A17] border border-[#EAE3D9] dark:border-[#332D29] text-[14px] font-medium text-[#2A2A2A] dark:text-[#F5F2ED] whitespace-nowrap shadow-[0_2px_8px_rgba(42,42,42,0.03)]"
              data-testid={`mobile-chip-${c.slug || c.id}`}
            >
              <IconForCategory icon={c.icon} className="h-[16px] w-[16px] text-[#E85A4F]" />
              {language === 'es' ? (c.name_es || c.name) : (c.name_en || c.name_es || c.name)}
            </motion.button>
          ))}
          {!chipCategories.length && (
            <div className="shrink-0 h-11 w-32 rounded-xl bg-white/40 animate-pulse" />
          )}
        </div>
      </div>

      {/* ── 4. Featured Businesses Carousel ─────────────────────── */}
      <div className="mt-8 relative z-10">
        <div className="px-5 flex items-center justify-between mb-3">
          <h2 className="font-display font-semibold text-[17px] text-[#2A2A2A] dark:text-[#F5F2ED]">
            {language === 'es' ? 'Negocios destacados' : 'Featured spots'}
          </h2>
          <button
            onClick={() => navigate('/search')}
            className="text-[13px] font-semibold text-[#E85A4F]"
            data-testid="mobile-featured-see-all"
          >
            {language === 'es' ? 'Ver todos' : 'See all'}
          </button>
        </div>
        <div
          className="flex gap-3 overflow-x-auto snap-x snap-mandatory pl-5 pr-5 pb-2 scrollbar-hide"
          data-testid="mobile-featured-list"
        >
          {(featuredBusinesses || []).slice(0, 8).map((b) => (
            <FeaturedCard key={b.id} business={b} onClick={() => onFeaturedClick?.(b)} />
          ))}
          {!(featuredBusinesses || []).length && (
            <div className="w-full px-1">
              <div className="rounded-2xl border border-dashed border-[#EAE3D9] dark:border-[#332D29] bg-white/60 dark:bg-white/5 px-4 py-6 text-center" data-testid="mobile-featured-empty">
                <div className="mx-auto h-11 w-11 rounded-full bg-[#E85A4F]/10 grid place-items-center mb-2">
                  <MapPin className="h-5 w-5 text-[#E85A4F]" strokeWidth={2.2} />
                </div>
                <p className="font-display font-semibold text-[15px] text-[#2A2A2A] dark:text-[#F5F2ED]">
                  {city
                    ? (language === 'es' ? `Aún no hay negocios destacados en ${city}` : `No featured spots in ${city} yet`)
                    : (language === 'es' ? 'Aún no hay negocios destacados' : 'No featured spots yet')}
                </p>
                <p className="text-[12px] text-[#737373] dark:text-[#A39F99] mt-1 leading-snug">
                  {language === 'es'
                    ? 'Prueba otra ciudad o explora las categorías.'
                    : 'Try another city or browse the categories.'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── 5. Top Rated ─────────────────────────────────────────── */}
      {topRated.length > 0 && (
        <div className="mt-8 relative z-10">
          <div className="px-5 flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#FFD166] to-[#F4A261] flex items-center justify-center shrink-0">
                <Star className="h-4 w-4 text-white fill-white" />
              </div>
              <h2 className="font-display font-semibold text-[17px] text-[#2A2A2A] dark:text-[#F5F2ED] truncate">
                {language === 'es' ? 'Los mejor valorados' : 'Top rated'}
              </h2>
            </div>
            <button
              onClick={() => navigate('/search?sort=rating')}
              className="text-[13px] font-semibold text-[#E85A4F] shrink-0"
              data-testid="mobile-toprated-see-all"
            >
              {language === 'es' ? 'Ver todos' : 'See all'}
            </button>
          </div>
          <div
            className="flex gap-3 overflow-x-auto snap-x snap-mandatory pl-5 pr-5 pb-2 scrollbar-hide"
            data-testid="mobile-toprated-list"
          >
            {topRated.map((b) => (
              <FeaturedCard key={b.id} business={b} onClick={() => onFeaturedClick?.(b)} />
            ))}
          </div>
        </div>
      )}

      {/* ── 6. Business CTA (supply-side growth) ─────────────────── */}
      <div className="mt-10 relative z-10 px-5">
        <motion.button
          whileTap={{ scale: 0.98 }}
          onClick={() => openExternalBookviaFlow(navigate, '/business/register')}
          className="w-full text-left rounded-2xl p-5 bg-gradient-to-br from-[#E85A4F] to-[#C94A3F] text-white shadow-[0_10px_30px_rgba(232,90,79,0.28)] relative overflow-hidden"
          data-testid="mobile-biz-cta"
        >
          {/* Decorative blobs */}
          <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-white/10" aria-hidden="true" />
          <div className="absolute -right-4 bottom-0 h-20 w-20 rounded-full bg-white/10" aria-hidden="true" />

          <div className="relative flex items-start gap-4">
            <div className="h-12 w-12 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
              <Sparkles className="h-6 w-6 text-white" strokeWidth={2.2} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] uppercase tracking-widest font-semibold opacity-80 mb-1">
                {language === 'es' ? 'Para negocios' : 'For businesses'}
              </p>
              <h3 className="font-display font-bold text-[19px] leading-tight mb-1">
                {language === 'es' ? '¿Tienes un negocio?' : 'Own a business?'}
              </h3>
              <p className="text-[13px] leading-snug opacity-90">
                {language === 'es'
                  ? 'Únete a Bookvia y recibe reservas 24/7 sin comisiones ocultas.'
                  : 'Join Bookvia and get bookings 24/7 with no hidden fees.'}
              </p>
              <div className="mt-3 inline-flex items-center gap-1.5 bg-white text-[#E85A4F] rounded-full px-3.5 py-1.5 text-[12px] font-bold">
                {language === 'es' ? 'Registrar mi negocio' : 'Register my business'}
                <span aria-hidden="true">→</span>
              </div>
            </div>
          </div>
        </motion.button>
      </div>

      {/* ── 7. ¿Cómo funciona Bookvia? (educational, always visible) ─ */}
      <div className="mt-10 relative z-10 px-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="h-8 w-8 rounded-full bg-[#E85A4F]/10 flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-[#E85A4F]" />
          </div>
          <h2 className="font-display font-semibold text-[17px] text-[#2A2A2A] dark:text-[#F5F2ED]">
            {language === 'es' ? '¿Cómo funciona Bookvia?' : 'How Bookvia works'}
          </h2>
        </div>

        <ol className="space-y-3">
          {[
            {
              n: '01',
              icon: Search,
              t_es: 'Explora',
              t_en: 'Explore',
              d_es: 'Descubre negocios y servicios cerca de ti. Filtra por precio, categoría y horarios.',
              d_en: 'Discover businesses and services near you. Filter by price, category and hours.',
            },
            {
              n: '02',
              icon: LucideIcons.CalendarClock,
              t_es: 'Reserva',
              t_en: 'Book',
              d_es: 'Elige día y hora en segundos. Confirma con anticipo si aplica, sin llamadas.',
              d_en: 'Pick a day and time in seconds. Pay deposit if needed — no calls required.',
            },
            {
              n: '03',
              icon: LucideIcons.CheckCircle2,
              t_es: 'Disfruta',
              t_en: 'Enjoy',
              d_es: 'Llega a tu cita, disfruta el servicio y deja una reseña para ayudar a otros.',
              d_en: 'Show up, enjoy the service, and leave a review to help others.',
            },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <li
                key={s.n}
                className="flex items-start gap-3 p-4 rounded-2xl bg-white dark:bg-[#1E1A17] border border-[#EAE3D9] dark:border-[#332D29] shadow-[0_2px_10px_rgba(42,42,42,0.03)]"
                data-testid={`mobile-hiw-step-${s.n}`}
              >
                <div className="shrink-0 h-11 w-11 rounded-xl bg-[#E85A4F]/10 flex items-center justify-center relative">
                  <Icon className="h-5 w-5 text-[#E85A4F]" strokeWidth={2.2} />
                  <span className="absolute -top-1 -right-1 text-[9px] font-bold text-white bg-[#E85A4F] rounded-full h-4 w-4 flex items-center justify-center">
                    {s.n.replace('0', '')}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display font-semibold text-[15px] text-[#2A2A2A] dark:text-[#F5F2ED] leading-tight">
                    {language === 'es' ? s.t_es : s.t_en}
                  </p>
                  <p className="mt-1 text-[13px] leading-snug text-[#737373] dark:text-[#A39F99]">
                    {language === 'es' ? s.d_es : s.d_en}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      {/* ── 8. Trust footer (Bookvia signature) ────────────────────── */}
      <div className="mt-8 mb-2 relative z-10 px-5">
        <div className="text-center">
          <div className="inline-flex items-center gap-1.5 text-[#E85A4F] font-display font-bold text-[15px]">
            <span>Bookvia</span>
            <Sparkles className="h-3.5 w-3.5" />
          </div>
          <p className="mt-1 text-[11px] text-[#A39F99] dark:text-[#5F5852]">
            {language === 'es'
              ? 'Reserva servicios locales con confianza.'
              : 'Book local services with confidence.'}
          </p>
        </div>
      </div>
    </div>
  );
}

export default MobileHomeExperience;
